import { useState, useEffect, useRef } from 'react';

/**
 * useElapsedTimer
 *
 * Tracks elapsed seconds while `active` is true.
 * Resets to 0 the moment `active` flips to true again.
 * Cleans up automatically on unmount.
 *
 * @param {boolean}          active      - start (true) / stop (false)
 * @param {(s: number)=>void} [onTick]   - optional callback, called on every tick
 * @param {number}           [intervalMs=500] - polling interval in ms
 * @returns {number}  elapsed seconds
 *
 * @example
 *   const elapsed = useElapsedTimer(isLoading, onElapsedChange);
 */
export function useElapsedTimer(active, onTick, intervalMs = 500) {
  const [elapsed, setElapsed] = useState(0);
  const startRef  = useRef(null);
  const timerRef  = useRef(null);
  // Keep onTick stable so the effect doesn't restart on every render
  const onTickRef = useRef(onTick);
  useEffect(() => { onTickRef.current = onTick; });

  useEffect(() => {
    if (active) {
      startRef.current = Date.now();
      setElapsed(0);
      timerRef.current = setInterval(() => {
        const s = Math.round((Date.now() - startRef.current) / 1000);
        setElapsed(s);
        onTickRef.current?.(s);
      }, intervalMs);
    } else {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    return () => clearInterval(timerRef.current);
  }, [active, intervalMs]);

  return elapsed;
}

export default useElapsedTimer;
